/**
 * Navigation utilities for TanStack Router
 *
 * Provides type-safe helpers for navigating to dynamic paths
 * that aren't statically known at compile time (e.g., redirect URLs from search params).
 */

/**
 * Cast a dynamic path string for use with TanStack Router's navigate function.
 * This is a cleaner alternative to `as '/'` assertions scattered throughout the codebase.
 *
 * TanStack Router's navigate() expects paths from the registered route tree,
 * but sometimes we need to navigate to dynamic paths (e.g., redirect URLs from search params).
 * This function provides a semantic way to handle that.
 *
 * @example
 * // Instead of: navigate({ to: redirectPath as '/' })
 * // Use: navigate({ to: dynamicPath(redirectPath) })
 */
export function dynamicPath(path: string): '/' {
  return path as '/'
}

/**
 * Split a redirect path that may contain a query string into the pieces
 * TanStack Router's `navigate()` expects: a path-only `to` value and a
 * `search` object. TanStack treats `to` as path-only — any `?key=value`
 * baked into the string is silently dropped — so email deep-links that
 * need to survive a login round-trip (e.g.
 * `/properties/$id/offers?offerId=…`) must be split before they're
 * passed to navigate.
 *
 * @example
 * const { pathname, search } = parseRedirectPath('/x/y?z=1')
 * // pathname === '/x/y', search === { z: '1' }
 */
export function parseRedirectPath(redirect: string): {
  pathname: string
  search: Record<string, string>
} {
  const queryIndex = redirect.indexOf('?')
  if (queryIndex === -1) {
    return { pathname: redirect, search: {} }
  }
  return {
    pathname: redirect.slice(0, queryIndex),
    search: Object.fromEntries(new URLSearchParams(redirect.slice(queryIndex + 1))),
  }
}
